/**
 * What saving a schedule edit will do, in the operator's words — the
 * live line under the schedule editor (`ScheduleEditDrawer`).
 *
 * Every fact here comes from the api-server's dry run
 * (`POST .../schedules/{id}/preview`, api-server 0.6.54), which is the
 * PATCH's own computation stopped short of the write: the first new run,
 * the skips and moves the new pattern drops, a run later today it
 * loses. Nothing is re-derived in the browser. The re-anchor rules —
 * the cut, the "never twice in a day" rule, a COUNT carried over — live
 * in one place (`scheduler.plan_schedule_edit`), and a second copy here
 * would be the one that drifts.
 *
 * And the runs the edit KEEPS at the old time. A schedule is one rule,
 * so when the first new run is on a later day than old runs still to
 * come, the server keeps those on a second schedule that ends the night
 * before (the carry-over, `kept_runs_ms` / `kept_until_ms`): "from
 * Wednesday" on a Friday no longer means no run Saturday to Tuesday, and
 * 15:00 -> 12:00 at 13:30 no longer costs today's 15:00. The summary
 * says what is kept, so the second schedule in the lists is expected.
 *
 * And the other rows of the plan it cuts short or removes (`clipped`):
 * after an earlier change the schedule is listed more than once, and a
 * new time from a day on replaces the old time an earlier row still
 * had from that day -- said by that row's name, time and end, so the
 * operator knows which of the listed rows changes.
 *
 * Pure, so the sentences are tested without a server.
 */
import type { ClippedSchedule, ScheduleUpdatePreview, TaskSchedule } from '../../api/v1';
import { addDays, dateKeyOf, instantAt, WEEKDAY_SHORT, type DateKey } from './calendar/lane-time';
import { dayText, endText, hasEnded, samePlan, stillToRunText } from './calendar/schedule-span';
import { cleanModeLabel, cleanModeOfRequest } from './clean-modes';
import { describeRule } from './rrule';
import { locationFromRequest } from './task-location';
import { PRIORITY_LABEL } from './task-priority';
import { priorityLevel } from '../../api/task-priority';
import { formatZonedClock, formatZonedDate, zonedWeekday } from './zoned-time';

export type SummaryTone = 'plain' | 'warn';

export interface SummaryLine {
  readonly id:
    | 'headline'
    | 'past'
    | 'next'
    | 'dropped'
    | 'today'
    | 'kept'
    | 'clipped'
    | 'unchanged'
    | 'count'
    | 'details'
    | 'rule'
    | 'paused';
  readonly text: string;
  readonly tone: SummaryTone;
}

export interface EditSummaryInput {
  /** The schedule as the editor opened it. */
  readonly before: TaskSchedule;
  /** The dry run's answer for the body the form holds now. */
  readonly preview: ScheduleUpdatePreview;
  /** The body changes the time or pattern. The server can still answer
   *  `timing_change: false` -- the new time would never run before the
   *  end, so it keeps the old one -- and that is said, not hidden. */
  readonly timingAsked: boolean;
  readonly nowMs: number;
  /** The SCHEDULE's own zone: every date and clock here is on it. */
  readonly timeZone: string;
  /** The site's zone, so a rule in it is described without the zone. */
  readonly siteTimeZone: string;
  /** Whether the schedule has a past (`hasScheduleHistory`). */
  readonly hasHistory: boolean;
  /** The "Applies from" day, for a timing change to a schedule with a
   *  past; null otherwise. */
  readonly applyFromKey: DateKey | null;
  /** The operator changed "after N runs" (not just kept it). */
  readonly countChanged: boolean;
  /** The site's schedules as listed now, to name the other rows of the
   *  plan a retime cuts short by their time and end. */
  readonly listed?: readonly TaskSchedule[];
}

/**
 * Whether a schedule has a past the server will keep: its start is
 * already behind us, or it has run (`last_run_ms`, the fire ledger's
 * word). The second matters as much as the first: a retime moves the
 * stored start onto the first NEW run, often days ahead, and a run
 * started early ("Run next now") counts too. An api-server older than
 * 0.6.54 sends no `last_run_ms`, and only the start is read.
 */
export function hasScheduleHistory(
  s: Pick<TaskSchedule, 'dtstart_ms' | 'last_run_ms'>,
  nowMs: number,
): boolean {
  return s.dtstart_ms < nowMs || (s.last_run_ms ?? null) !== null;
}

/**
 * The `apply_from_ms` to send for the "Applies from" day: nothing for
 * today (or earlier), which the server reads as "from now"; else the
 * start of that day on the schedule's calendar. The server still lifts
 * the cut to now and never reruns a run already recorded, so no value
 * sent here can rewrite a run that already happened.
 */
export function applyFromMsFor(key: DateKey, todayKey: DateKey, timeZone: string): number | null {
  return key <= todayKey ? null : instantAt(key, 0, timeZone);
}

/** "Tue 04/Nov/2026". */
export function dayDateText(ms: number, timeZone: string): string {
  return `${WEEKDAY_SHORT[zonedWeekday(ms, timeZone)]} ${formatZonedDate(ms, timeZone)}`;
}

/** "Today 10:00", "Tomorrow 10:00", else "Tue 04/Nov 10:00" -- with
 *  the year when it is not this year ("Mon 04/Jan/2027 12:00"), the
 *  rule every other date in the summary follows (`dayText`). */
export function runText(ms: number, nowMs: number, timeZone: string): string {
  const today = dateKeyOf(nowMs, timeZone);
  const key = dateKeyOf(ms, timeZone);
  const clock = formatZonedClock(ms, timeZone);
  if (key === today) return `Today ${clock}`;
  if (key === addDays(today, 1)) return `Tomorrow ${clock}`;
  return `${dayText(ms, timeZone, nowMs)} ${clock}`;
}

function lowerFirst(s: string): string {
  return s ? s[0]!.toLowerCase() + s.slice(1) : s;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** A rule without its COUNT, so "10 times" carried over as "7 times"
 *  does not read as a change of pattern. */
function withoutCount(rrule: string): string {
  return rrule
    .split(';')
    .filter((c) => !/^\s*COUNT\s*=/i.test(c))
    .join(';');
}

/**
 * "(was 09:00)" when only the time of day moved, "(was from Wed
 * 04/Nov/2026)" when only the start day did -- the whole old pattern
 * would read the same as the new one -- else the whole old pattern: the
 * shortest thing that is still the truth.
 */
export function wasText(
  before: Pick<TaskSchedule, 'rrule' | 'dtstart_ms' | 'until_ms'>,
  after: Pick<TaskSchedule, 'rrule' | 'dtstart_ms' | 'until_ms'>,
  timeZone: string,
  siteTimeZone: string,
): string {
  const opts = { siteTimeZone };
  const oldHead = describeRule(withoutCount(before.rrule), before.dtstart_ms, null, timeZone, opts);
  const newHead = describeRule(withoutCount(after.rrule), after.dtstart_ms, null, timeZone, opts);
  const oldClock = formatZonedClock(before.dtstart_ms, timeZone);
  const newClock = formatZonedClock(after.dtstart_ms, timeZone);
  if (oldClock !== newClock && oldHead.replace(oldClock, newClock) === newHead) {
    return `was ${oldClock}`;
  }
  if (oldHead === newHead && before.dtstart_ms !== after.dtstart_ms) {
    return `was from ${dayDateText(before.dtstart_ms, timeZone)}`;
  }
  return `was ${lowerFirst(describeRule(before.rrule, before.dtstart_ms, before.until_ms, timeZone, opts))}`;
}

function listWords(items: readonly string[]): string {
  return items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * The old runs a timing change keeps before its first new day, from the
 * dry run (`kept_runs_ms`, `kept_until_ms`): "Today's 15:00 run keeps
 * its old time." when they are all today, else "Until Tue 03/Nov it
 * keeps its old time (4 runs at 15:00)." The server lists five at most,
 * so five reads as "5 or more". Null when it keeps none.
 */
export function keptText(
  keptRunsMs: readonly number[],
  keptUntilMs: number | null,
  nowMs: number,
  timeZone: string,
): string | null {
  if (keptRunsMs.length === 0) return null;
  const today = dateKeyOf(nowMs, timeZone);
  const clocks = keptRunsMs.map((t) => formatZonedClock(t, timeZone));
  const allToday =
    keptRunsMs.length < 5 &&
    keptRunsMs.every((t) => dateKeyOf(t, timeZone) === today) &&
    (keptUntilMs === null || dateKeyOf(keptUntilMs, timeZone) === today);
  if (allToday) {
    return clocks.length === 1
      ? `Today's ${clocks[0]} run keeps its old time.`
      : `Today's ${listWords(clocks)} runs keep their old time.`;
  }
  const last = keptRunsMs[keptRunsMs.length - 1]!;
  // The server sends the end with the runs; the last run's day is only
  // a fallback for an answer without it.
  const until =
    keptUntilMs !== null
      ? endText(keptUntilMs, timeZone, nowMs)
      : `until ${dayText(last, timeZone, nowMs)}`;
  const count =
    keptRunsMs.length >= 5 ? '5 or more runs' : plural(keptRunsMs.length, 'run', 'runs');
  const at = new Set(clocks).size === 1 ? ` at ${clocks[0]}` : '';
  return `${until[0]!.toUpperCase()}${until.slice(1)} it keeps its old time (${count}${at}).`;
}

/**
 * The other rows of the plan a retime cuts short or removes, from the
 * dry run (`clipped`), one sentence each: `The other "Vacuum ·
 * L4-Lobby" (every day at 09:00, until Tue 03/Nov) now ends Fri 30/Oct:
 * from Sat 31/Oct every run is at 08:00. It loses 3 runs at 09:00.` --
 * "is replaced" for one saving removes (it has never run and keeps no
 * run). Each is named as the lists show it (`listed`, `stillToRunText`),
 * and "The other" only when the edited row (`editedId`) has one other
 * row still to run, as the delete dialog counts them; the new time is
 * the saved schedule's (`newStartMs`). Null when none.
 */
export function clippedText(
  clipped: readonly ClippedSchedule[],
  listed: readonly TaskSchedule[],
  newStartMs: number,
  nowMs: number,
  timeZone: string,
  siteTimeZone: string,
  editedId: string,
): string | null {
  if (clipped.length === 0) return null;
  const newClock = formatZonedClock(newStartMs, timeZone);
  const others = listed.filter(
    (x) =>
      x.schedule_id !== editedId &&
      samePlan(x, { schedule_id: editedId }) &&
      !hasEnded(x, nowMs),
  ).length;
  // The list not loaded: what the dry run names is all there is to go by.
  const one = others > 0 ? others === 1 : clipped.length === 1;
  return clipped
    .map((c) => {
      const o = listed.find((x) => x.schedule_id === c.schedule_id);
      const otz = o?.timezone || siteTimeZone;
      const oldClock = o ? formatZonedClock(o.dtstart_ms, otz) : null;
      const who = `${one ? 'The other' : 'Another'} "${c.name}"${
        o ? ` (${stillToRunText(o, nowMs, siteTimeZone)})` : ''
      }`;
      const changeMs = c.until_ms !== null ? c.until_ms + 1 : newStartMs;
      const from = `from ${dayText(changeMs, timeZone, nowMs)} every run is at ${newClock}.`;
      const what = c.removed
        ? 'is replaced'
        : c.until_ms! < nowMs
          ? 'stops now'
          : `now ends ${endText(c.until_ms!, otz, nowMs).replace(/^until /, '')}`;
      const lost =
        c.dropped_runs > 0
          ? ` It loses ${plural(c.dropped_runs, 'run', 'runs')}${oldClock ? ` at ${oldClock}` : ''}.`
          : '';
      return `${who} ${what}: ${from}${lost}`;
    })
    .join(' ');
}

/**
 * What a rename or a new clean type does to the runs that already
 * happened; null when the edit changes neither. A past run is listed
 * under its schedule as it is now -- the fire ledger links the task to
 * the schedule's id (`scheduleOfItem`) -- so it shows the NEW name. The
 * robot did the clean it was sent, and the calendar draws a past run
 * from that task, so it keeps the clean type it ran with. Neither is
 * the old label promise ("keep their name") nor the new one ("shows
 * them with the new clean type"): both were half true.
 */
export function relabelledText(
  before: Pick<TaskSchedule, 'name' | 'task'>,
  after: Pick<TaskSchedule, 'name' | 'task'>,
): { renamed: boolean; retyped: boolean } | null {
  const renamed = before.name !== after.name;
  const retyped = cleanModeOfRequest(before.task) !== cleanModeOfRequest(after.task);
  return renamed || retyped ? { renamed, retyped } : null;
}

/**
 * What a details edit changes, each as `What: new (was old)` -- the
 * form the timing headline uses -- so the summary says which thing
 * changed, not only that something did: `Clean mode: Scrub (was
 * Vacuum)`, `Where: L4-Lobby (was L4-Ballroom)`, `Name: "Lobby" (was
 * "Lobby vacuum")`, `Priority: High (was Normal)`. An end date is the
 * rule line's to say.
 */
export function detailChanges(
  before: Pick<TaskSchedule, 'name' | 'task' | 'category'>,
  after: Pick<TaskSchedule, 'name' | 'task' | 'category'>,
): string[] {
  const out: string[] = [];
  if (before.name !== after.name) out.push(`Name: "${after.name}" (was "${before.name}")`);
  const oldMode = cleanModeOfRequest(before.task);
  const newMode = cleanModeOfRequest(after.task);
  if (oldMode !== newMode && newMode !== undefined) {
    out.push(`Clean mode: ${cleanModeLabel(newMode)} (was ${cleanModeLabel(oldMode)})`);
  }
  const oldWhere = locationFromRequest(before.category, before.task)?.text ?? null;
  const newWhere = locationFromRequest(after.category, after.task)?.text ?? null;
  if (oldWhere !== newWhere && newWhere !== null) {
    out.push(`Where: ${newWhere}${oldWhere !== null ? ` (was ${oldWhere})` : ''}`);
  }
  // By level, so an old 1-5 value read back as Normal is not a change.
  const oldLevel = priorityLevel(before.task?.priority);
  const newLevel = priorityLevel(after.task?.priority);
  if (oldLevel !== newLevel) {
    out.push(`Priority: ${PRIORITY_LABEL[newLevel]} (was ${PRIORITY_LABEL[oldLevel]})`);
  }
  return out;
}

/** "Thu 05/Nov", "Thu 05/Nov and Sat 07/Nov", "Thu 05/Nov, Sat 07/Nov,
 *  Tue 10/Nov and 2 more": the days, on the schedule's clock. */
function daysText(ms: readonly number[], nowMs: number, timeZone: string): string {
  const days = [...new Set(ms.map((t) => dayText(t, timeZone, nowMs)))];
  const shown = days.length > 4 ? [...days.slice(0, 3), `${days.length - 3} more`] : days;
  return listWords(shown);
}

/**
 * What a timing change throws away, by day: a skip the new pattern does
 * not make is removed, so the robot runs that day as the new pattern
 * says -- the thing an operator who skipped the wedding day must not
 * learn from the calendar afterwards. "The skip on Thu 05/Nov is
 * removed: that day now runs as the new pattern says. Skip it again
 * after saving if it is still needed." A move is named by the day of the
 * run it moved. From counts alone when the server sends no instants (an
 * api-server before them). Null when nothing is dropped.
 */
export function droppedText(
  preview: Pick<
    ScheduleUpdatePreview,
    'dropped_skips' | 'dropped_moves' | 'dropped_skips_ms' | 'dropped_moves_ms'
  >,
  nowMs: number,
  timeZone: string,
): string | null {
  const skips = preview.dropped_skips;
  const moves = preview.dropped_moves;
  if (skips + moves === 0) return null;
  const skipDays = preview.dropped_skips_ms ?? [];
  const moveDays = preview.dropped_moves_ms ?? [];
  if (skipDays.length !== skips || moveDays.length !== moves) {
    const parts = [
      skips > 0 ? plural(skips, 'skipped day', 'skipped days') : null,
      moves > 0 ? plural(moves, 'moved run', 'moved runs') : null,
    ].filter((p): p is string => p !== null);
    const one = skips + moves === 1;
    return `${parts.join(' and ')} after the change no longer ${one ? 'applies' : 'apply'}: ${one ? 'that day runs' : 'those days run'} as the new pattern says.`;
  }
  const out: string[] = [];
  if (skips > 0) {
    out.push(
      skips === 1
        ? `The skip on ${daysText(skipDays, nowMs, timeZone)} is removed: that day now runs as the new pattern says. Skip it again after saving if it is still needed.`
        : `The skips on ${daysText(skipDays, nowMs, timeZone)} are removed: those days now run as the new pattern says. Skip them again after saving if they are still needed.`,
    );
  }
  if (moves > 0) {
    out.push(
      moves === 1
        ? `The move of the ${daysText(moveDays, nowMs, timeZone)} run is undone: it runs as the new pattern says.`
        : `The moves of the runs on ${daysText(moveDays, nowMs, timeZone)} are undone: they run as the new pattern says.`,
    );
  }
  return out.join(' ');
}

/** The live summary, line by line. */
export function editSummary(input: EditSummaryInput): SummaryLine[] {
  const { before, preview, nowMs, timeZone: tz, siteTimeZone } = input;
  const saved = preview.schedule;
  const lines: SummaryLine[] = [];
  const paused = !before.enabled;
  const opts = { siteTimeZone };

  const relabelled = relabelledText(before, saved);
  if (!preview.timing_change) {
    const changed = detailChanges(before, saved)
      .map((c) => `${c}. `)
      .join('');
    lines.push({
      id: 'details',
      text: `${changed}Applies to every run from now.${
        relabelled === null
          ? ''
          : relabelled.renamed && relabelled.retyped
            ? ' The calendar shows past runs under the new name; they keep the clean type they ran with.'
            : relabelled.renamed
              ? ' The calendar shows past runs under the new name too.'
              : ' Runs that already happened keep the clean type they ran with.'
      }`,
      tone: 'plain',
    });
    if ((before.until_ms ?? null) !== (saved.until_ms ?? null)) {
      lines.push({
        id: 'rule',
        text: `${describeRule(saved.rrule, saved.dtstart_ms, saved.until_ms, tz, opts)}.`,
        tone: 'plain',
      });
    }
    if (input.timingAsked) {
      // The server's fallback: the new time would make no run before
      // the end, so the old one stays, under the new end.
      lines.push({
        id: 'unchanged',
        text: 'It ends before the new time would first run, so it keeps its old time until then.',
        tone: 'warn',
      });
    }
  } else {
    const rule = describeRule(saved.rrule, saved.dtstart_ms, saved.until_ms, tz, opts);
    const was = wasText(before, saved, tz, siteTimeZone);
    lines.push({
      id: 'headline',
      text: input.hasHistory
        ? `From ${dayDateText(saved.dtstart_ms, tz)}: ${rule} (${was}).`
        : `${rule} (${was}).`,
      tone: 'plain',
    });
    lines.push({
      id: 'past',
      text: input.hasHistory
        ? `Runs that already happened keep their ${
            relabelled?.retyped ? 'time, result and clean type' : 'time and result'
          }${relabelled?.renamed ? '; the calendar shows them under the new name' : ''}.`
        : 'It has not run yet, so this applies to every run.',
      tone: 'plain',
    });
  }

  // What it keeps comes before what it changes to, in the order the
  // runs happen; with runs kept, the edited schedule's next run is not
  // the next run at all, so it is named as the first at the new time.
  const kept = preview.timing_change
    ? keptText(preview.kept_runs_ms ?? [], preview.kept_until_ms ?? null, nowMs, tz)
    : null;
  if (kept !== null) lines.push({ id: 'kept', text: kept, tone: 'plain' });

  const clipped = preview.timing_change
    ? clippedText(
        preview.clipped ?? [],
        input.listed ?? [],
        saved.dtstart_ms,
        nowMs,
        tz,
        siteTimeZone,
        saved.schedule_id,
      )
    : null;
  if (clipped !== null) {
    const lost = (preview.clipped ?? []).some((c) => c.dropped_runs > 0 || c.removed);
    lines.push({ id: 'clipped', text: clipped, tone: lost ? 'warn' : 'plain' });
  }

  if (preview.first_run_ms !== null) {
    const label =
      kept !== null
        ? paused
          ? 'First run at the new time, when resumed'
          : 'First run at the new time'
        : paused
          ? 'Next run when resumed'
          : 'Next run';
    lines.push({
      id: 'next',
      text: `${label}: ${runText(preview.first_run_ms, nowMs, tz)}.`,
      tone: 'plain',
    });
  } else {
    lines.push({ id: 'next', text: 'No runs are left after this change.', tone: 'warn' });
  }

  if (preview.timing_change && input.countChanged && input.hasHistory) {
    lines.push({
      id: 'count',
      text: 'The number of runs counts again from the first run after the change.',
      tone: 'plain',
    });
  }

  const dropped = droppedText(preview, nowMs, tz);
  if (dropped !== null) lines.push({ id: 'dropped', text: dropped, tone: 'warn' });

  if (preview.lost_today_ms !== null) {
    const lost = formatZonedClock(preview.lost_today_ms, tz);
    const sameDay =
      preview.first_run_ms !== null &&
      dateKeyOf(preview.first_run_ms, tz) === dateKeyOf(preview.lost_today_ms, tz);
    lines.push({
      id: 'today',
      text: sameDay
        ? `Today's ${lost} run moves to ${formatZonedClock(preview.first_run_ms!, tz)}.`
        : `Today's ${lost} run won't happen.`,
      tone: sameDay ? 'plain' : 'warn',
    });
  }

  if (paused) {
    lines.push({ id: 'paused', text: 'Paused: nothing runs until it is resumed.', tone: 'plain' });
  }
  return lines;
}
